import { LoadingSpinner } from "components/LoadingSpinner";
import {
  getDefaultPracticeTypeCode,
  mapPayerNameToCodeForDropdown,
} from "constants/practiceType";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty } from "lodash";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IAccount } from "types/Account/Account";
import {
  IOrder,
  IGFEServiceDetails,
  IFacilityRules,
  INonFacilityRules,
} from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { deriveStoredEligibilityMessage } from "../eligibilityMessages";
import {
  readNoDeductibleChoice,
  setNoDeductibleChoice,
} from "../noDeductibleChoice";
import { Benefits } from "./Benefits";
import { EligibilityButton } from "./EligibilityButton";
import { PracticeTypeSwitchNotice } from "./PracticeTypeSwitchNotice";

const StyledEligibilityButtonContainer = styled.div`
  @media (min-width: 992px) {
    margin-top: 28px;
  }
`;

const StyledMessageContainer = styled.div`
  border: 1px solid var(--gray-new-200);
  border-left: none;
  border-right: none;
  background-color: #f9fafb;
  padding: 22px 24px;
  color: #dc6803;
  display: flex;
  align-items: center;
  gap: ${sizer(2)};
`;

export function PrimaryInsuranceSection({
  loadingPayersName,
  payersNameInfo,
  payersCodesInfo,
  redo = false,
  draft = false,
  copy = false,
  isEligibilityEnabledForMainProvider,
  accountId,
  mainProvider,
  costApplicationRules,
  account,
  draftOrder,
  orderToRedo,
  orderToCopy,
}: {
  loadingPayersName: boolean;
  payersNameInfo: any[];
  payersCodesInfo: any[];
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
  isEligibilityEnabledForMainProvider?: boolean;
  accountId: string;
  mainProvider: IProvider;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  account: IAccount;
  draftOrder?: IOrder;
  orderToRedo?: IOrder;
  orderToCopy?: IOrder;
}) {
  const {
    control,
    trigger,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(
    null
  );
  const [showMemberIdAndEligibility, setShowMemberIdAndEligibility] =
    useState<boolean>(true);

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  const payerNameField = watch("payerName");
  const payerField = watch("payer");
  const patientFirstNameField = watch("firstName");
  const patientLastNameField = watch("lastName");
  const patientDOBField = watch("dateOfBirth");
  const memberIdField = watch("memberId");
  const practiceTypeField = watch("practiceType");
  const isSubscriberPatientCheckbox = watch("isSubscriberPatient");
  const benefitDetailsField = watch("benefitDetails");

  /**
   * CLE-2661 — removed: this forced practice type "13" for HNE payers 51 and 55, believing it meant
   * "ASC Facility". It does not. 13 is the SERVICE type code for ASC; as a PRACTICE type code pVerify
   * resolves it to Hospital-InPatient. Across all 521 stored responses sent with it, an
   * ASC_FacilitySummary is present on 0 and an "Ambulatory Service Center Facility" service line on 1
   * — but a "Hospital - Ambulatory Surgical" line is present on 488, and the ASC copay chain reads
   * that as its second rung. So these responses do produce a copay: the HOSPITAL ambulatory-surgical
   * one, quoted to a patient at a freestanding surgery centre.
   *
   * That is the same discrepancy CLE-2540 tabulated by hand for HNE ("HNE reports the hospital rate
   * for its Medicare Advantage plans"), which makes this override a candidate explanation for it —
   * the one thing meant to get HNE better ASC data was asking the wrong question. Candidate, not
   * conclusion: confirming it needs live responses on a correct practice type.
   *
   * Nothing replaces it here. HNE's account picks its own practice type via
   * `Account.defaultPracticeTypeCode`, which is how every other account does it; a payer-specific
   * override hidden in a form component is what made this invisible for so long. The CLE-2540 copay
   * table is deliberately left in place — whether real ASC benefits now come back is a question for
   * live responses, not something to assume while removing the thing that blocked them.
   */

  /**
   * CLE-2661 — fill the service type from the account when the form has none.
   *
   * This has to happen here rather than in each page's `useForm` defaults: those are evaluated on the
   * first render, when the account is usually still loading, so an account default read there would
   * silently resolve to the global fallback and never correct itself. `PatientBenefitsSection` only
   * mounts once account data has arrived, so by this point `account` is real.
   *
   * The `!practiceTypeField` guard is what makes it safe on redo / copy / draft: those pages seed the
   * field from the order's own recorded practice type, and a re-run must ask the same question the
   * original inquiry asked. Only a genuinely empty field is filled. Mirrors practiceType2 in
   * SecondaryInsuranceSection.
   */
  useEffect(() => {
    if (!practiceTypeField) {
      setValue(
        "practiceType",
        getDefaultPracticeTypeCode(account?.defaultPracticeTypeCode)
      );
    }
  }, [practiceTypeField, setValue, account?.defaultPracticeTypeCode]);

  useEffect(() => {
    if ((payerField?.name && payerField?.payerId) || loadingPayersName) return;
    const applyPayer = (payerName: string, payerId: number) => {
      setValue("payerName", payerName);
      setValue("payer", { name: payerName, payerId });
    };

    const getPayerFromServiceDetails = (payerName?: string) => {
      if (!payerName && isEmpty(payersNameInfo)) {
        return { name: null, payerId: null };
      }
      const name = payerName || payersNameInfo[0]?.value?.name;
      const payerId =
        payerName &&
        payersNameInfo?.find((payer: any) => payer.name === payerName)?.value
          .payerId
          ? payersNameInfo.find((payer: any) => payer.name === payerName)?.value
              .payerId
          : payersNameInfo[0].value.payerId;

      return { name, payerId };
    };

    const processOrder = (order: any) => {
      const savedPayerId = order?.payerId;
      if (savedPayerId) {
        applyPayer(order?.payer?.name, order?.payer?.id);
      } else {
        const payerNameFromDetails = (
          order?.serviceDetails as IGFEServiceDetails
        )?.payerName;
        const { name, payerId } =
          getPayerFromServiceDetails(payerNameFromDetails);
        if (!name && !payerId) {
          return;
        }
        applyPayer(name, payerId);
      }
    };
    if (redo) {
      processOrder(orderToRedo);
    } else if (copy) {
      processOrder(orderToCopy);
    } else if (draft) {
      processOrder(draftOrder);
    } else {
      applyPayer(
        payersNameInfo[0]?.value?.name,
        payersNameInfo[0]?.value?.payerId
      );
    }
  }, [payerField, payersNameInfo, redo, copy, draft]);

  useEffect(() => {
    if (payerNameField === "Self-Pay Rate") {
      setShowMemberIdAndEligibility(false);
      setValue("deductible", 0);
      setValue("outOfPocketMax", 100000);
      setValue("flatCopay", 0);
      setValue("coinsurancePercent", 100);
    } else {
      setShowMemberIdAndEligibility(true);
    }
  }, [payerNameField]);

  // Set eligibility message based on the stored benefitDetails response (inactive plan,
  // empty benefits, out-of-network-only — see deriveStoredEligibilityMessage).
  // NOTE: Don't clear the message if recommendedPayerBasedOnEligibility is present,
  // as that message is set by EligibilityButton and should persist (CLE-1221).
  useEffect(() => {
    const response = benefitDetailsField?.response;
    if (!response) {
      return;
    }
    // If we detected Medicare Advantage and recommended a payer swap, don't overwrite that message
    if (response.recommendedPayerBasedOnEligibility) {
      return;
    }
    setEligibilityMessage(deriveStoredEligibilityMessage(response));
  }, [benefitDetailsField]);

  useEffect(() => {
    const draftOrderCheck =
      draft &&
      (!draftOrder?.benefitDetails || !draftOrder?.benefitDetails?.response);
    const redoOrderCheck =
      redo &&
      (!orderToRedo?.benefitDetails || !orderToRedo?.benefitDetails?.response);
    const orderToCopyCheck =
      copy &&
      (!orderToCopy?.benefitDetails || !orderToCopy?.benefitDetails?.response);
    if (
      loadingPayersName ||
      draftOrderCheck ||
      redoOrderCheck ||
      orderToCopyCheck ||
      (!draft && !redo && !copy)
    ) {
      setValue("benefitDetails", null);
      return;
    }
    let order = orderToRedo;
    if (draft) {
      order = draftOrder;
    } else if (copy) {
      order = orderToCopy;
    }
    const providerNpi = mainProvider?.npi;
    const eligibilityEnabled = mainProvider?.eligibilityEnabled;
    const eligibilityRequest = order?.benefitDetails?.request;

    /**
     * Discard stored benefits only when they describe SOMEONE ELSE — a different member, or a
     * different rendering provider. Those are the cases where showing them would put one patient's
     * deductible on another patient's estimate, so they stay strict.
     *
     * CLE-2661 — the practice type is deliberately NOT one of them. It is the same member and the
     * same payer; it only changes which care setting the payer was asked about, so stale-but-real
     * benefits are far more useful than a blank panel. Treating it as identity meant every order
     * whose stored request lacked the field showed "Please, run eligibility to get information"
     * with the benefits sitting right there in the database — which is exactly what the import
     * paths started producing when they stopped recording the code they sent.
     */
    const isForADifferentPatientOrProvider =
      eligibilityRequest?.member?.firstName !== patientFirstNameField ||
      eligibilityRequest?.member?.lastName !== patientLastNameField ||
      eligibilityRequest?.member?.dob !== patientDOBField ||
      eligibilityRequest?.member?.memberID !== memberIdField ||
      eligibilityRequest?.provider?.npi !== providerNpi ||
      eligibilityRequest?.isSubscriberPatient !== eligibilityEnabled;

    if (isForADifferentPatientOrProvider) {
      setValue("benefitDetails", null);
    } else {
      setValue("benefitDetails", order?.benefitDetails);
      // CLE-2996 — the stored response comes back with the "No deductible" choice it recorded. The
      // page's defaults seed it too, but while the payer list loads this effect blanks `benefitDetails` (above), and
      // Benefits takes the choice away with a cleared response — so it is put back here, with the response.
      if (readNoDeductibleChoice(order?.benefitDetails)) {
        setNoDeductibleChoice({ setValue, getValues }, true);
      }
      if (redo || draft || copy) {
        // Reopened orders re-show whatever warning their stored response warrants
        // (inactive plan, empty benefits, out-of-network-only — CLE-2675).
        const message = deriveStoredEligibilityMessage(
          order?.benefitDetails?.response
        );
        if (message) {
          setEligibilityMessage(message);
        }
      }
    }
  }, [
    patientFirstNameField,
    patientLastNameField,
    patientDOBField,
    memberIdField,
    loadingPayersName,
    payersCodesInfo,
    draftOrder,
    orderToRedo,
    orderToCopy,
    mainProvider,
  ]);

  return (
    <>
      <div className="formgrid grid flex-column lg:flex-row px-3">
        <div className="field flex flex-column col-12">
          <div className="formgrid grid">
            <div
              className="field flex flex-column col-12 md:col-4 lg:col-3"
              data-tour={TourAnchor.PayerName}
            >
              <Label htmlFor="payer">Payer Name</Label>

              <Controller
                control={control}
                name="payer"
                render={({ field: { onChange, value } }) =>
                  loadingPayersName ? (
                    <LoadingSpinner
                      customStyle={{ width: "50px", height: "100px" }}
                    />
                  ) : (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      placeholder="Any"
                      id="payer"
                      data-testid="payer_PayerForm"
                      options={payersNameInfo}
                      onChange={(e) => {
                        onChange(e.value);
                        setValue("payerName", e.value.name);
                        if (redo) {
                          setValue("redoOrderFirstRender", false);
                        }
                        if (draft) {
                          setValue("draftOrderFirstRender", false);
                        }
                        if (copy) {
                          setValue("copyOrderFirstRender", false);
                        }
                      }}
                    />
                  )
                }
              />
            </div>
            {isEligibilityEnabledForMainProvider && (
              <div
                className="field flex flex-column col-12 md:col-4 lg:col-3"
                data-tour={TourAnchor.ServiceType}
              >
                <Label htmlFor="practiceType">Service Type</Label>

                <Controller
                  control={control}
                  name="practiceType"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      data-testid="practiceType_PayerForm"
                      placeholder="Service Type"
                      id="practiceType"
                      options={mapPayerNameToCodeForDropdown}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            )}
            {isEligibilityEnabledForMainProvider &&
              showMemberIdAndEligibility && (
                <div
                  className="field flex flex-column col-12 md:col-4 lg:col-3"
                  data-tour={TourAnchor.MemberId}
                >
                  <Label htmlFor="memberId">Member ID</Label>

                  <Controller
                    control={control}
                    name="memberId"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="memberId"
                          value={value || ""}
                          autoComplete="off"
                          data-testid="memberId_PayerForm"
                          placeholder="Member ID"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "memberId",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("memberId", errors)}
                      </>
                    )}
                  />
                </div>
              )}
            <StyledEligibilityButtonContainer className="field flex col-12 md:col-4 lg:col-3">
              {showMemberIdAndEligibility ? (
                <EligibilityButton
                  accountId={accountId}
                  mainProvider={mainProvider}
                  errorMessageSetter={setEligibilityMessage}
                  disabled={loadingPayersName}
                />
              ) : null}
            </StyledEligibilityButtonContainer>
          </div>
          {/* CLE-2971 — the API switched the Service Type from the order's CPT codes; "Keep …" undoes it. */}
          {isEligibilityEnabledForMainProvider &&
            showMemberIdAndEligibility && (
              <PracticeTypeSwitchNotice accountId={accountId} />
            )}
          {isEligibilityEnabledForMainProvider &&
            !isSubscriberPatientCheckbox && (
              <div className="formgrid grid">
                <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                  <Label htmlFor="subscriberFirstName">
                    Subscriber First Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberFirstName"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberFirstName"
                          data-testid="subscriberFirstName_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberFirstName",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberFirstName", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                  <Label htmlFor="subscriberLastName">
                    Subscriber Last Name
                  </Label>

                  <Controller
                    control={control}
                    name="subscriberLastName"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="subscriberLastName"
                          data-testid="subscriberLastName_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Subscriber Name"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberLastName",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("subscriberLastName", errors)}
                      </>
                    )}
                  />
                </div>
                <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                  <Label htmlFor="subscriberDOB">Subscriber DOB</Label>

                  <Controller
                    name="subscriberDOB"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <>
                        <InputText
                          value={value || ""}
                          id="subscriberDOB"
                          data-testid="subscriberDOB_PayerForm"
                          placeholder="99/99/9999"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "subscriberDOB",
                              trigger,
                            })
                          }
                          onChange={(e) =>
                            handleChangeDateWithMask(e, onChange)
                          }
                        />

                        {getFormErrorMessage("subscriberDOB", errors)}
                      </>
                    )}
                  />
                </div>
              </div>
            )}
          {isEligibilityEnabledForMainProvider && (
            <div className="field flex-column col-12 md:col-4 lg:col-3 hidden">
              <Label htmlFor="groupNumber">Group Number</Label>

              <Controller
                control={control}
                name="groupNumber"
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputText
                      id="groupNumber"
                      data-testid="groupNumber_PayerForm"
                      value={value || ""}
                      autoComplete="off"
                      placeholder="Group Number"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "groupNumber",
                          trigger,
                        })
                      }
                      onChange={onChange}
                    />

                    {getFormErrorMessage("groupNumber", errors)}
                  </>
                )}
              />
            </div>
          )}
        </div>
      </div>
      <StyledMessageContainer>
        <i className="pi pi-info-circle" style={{ fontSize: "20px" }} />
        <p className="text-md font-semibold">
          Confirm benefits are correct by checking all location-specific options
        </p>
      </StyledMessageContainer>
      <Benefits
        redo={redo}
        draft={draft}
        copy={copy}
        costApplicationRules={costApplicationRules}
        account={account}
        eligibilityMessage={eligibilityMessage}
      />
    </>
  );
}
